import React, { useState } from 'react';

interface DefinitionItem {
    term: string;
    definition: string;
    formula?: string;
    example?: string;
    category: 'Energy' | 'Emissions' | 'Financial' | 'Legal' | 'Building';
}

interface TAMData {
    market: string;
    size: string;
    unit: string;
    description: string;
    opportunity: string;
    source: string;
}

const DefinitionsAndTAM: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'definitions' | 'tam'>('definitions');
    const [selectedCategory, setSelectedCategory] = useState<string>('All');

    const definitions: DefinitionItem[] = [
        // Energy Definitions
        {
            term: "Site EUI (Energy Use Intensity)",
            definition: "The amount of energy consumed at a building site, measured in thousands of British thermal units per square foot per year (kBtu/sq ft/year).",
            formula: "Site EUI = Total Site Energy Consumption ÷ Gross Floor Area",
            example: "A building consuming 2,500,000 kBtu annually with 50,000 sq ft has a Site EUI of 50 kBtu/sq ft/year",
            category: "Energy"
        },
        {
            term: "Source EUI",
            definition: "The total amount of raw fuel required to operate a building, including losses in generation, transmission, and distribution.",
            formula: "Source EUI = Site EUI × Source-to-Site Conversion Factor",
            example: "Site EUI of 50 with a conversion factor of 3.15 = Source EUI of 157.5 kBtu/sq ft/year",
            category: "Energy"
        },
        {
            term: "ENERGY STAR Score",
            definition: "A 1-100 rating comparing a building's energy performance to similar buildings nationwide, with 50 being median performance.",
            example: "Score of 75 means the building performs better than 75% of similar buildings",
            category: "Energy"
        },
        {
            term: "Weather Normalized EUI",
            definition: "Energy use intensity adjusted for local weather conditions, allowing for fair comparison across different climate zones and years.",
            category: "Energy"
        },

        // Emissions Definitions
        {
            term: "GHG Emissions (Greenhouse Gas)",
            definition: "Total carbon dioxide equivalent emissions from building operations, measured in metric tons CO2e per year.",
            formula: "GHG = Direct Emissions + Indirect Emissions",
            example: "150 tCO2e direct + 350 tCO2e indirect = 500 tCO2e total",
            category: "Emissions"
        },
        {
            term: "Direct GHG Emissions (Scope 1)",
            definition: "Emissions from fuel combusted on-site, typically from natural gas, oil, or propane for heating and hot water.",
            category: "Emissions"
        },
        {
            term: "Indirect GHG Emissions (Scope 2)",
            definition: "Emissions from purchased electricity, steam, heating, or cooling consumed by the building.",
            category: "Emissions"
        },
        {
            term: "LL97 Emission Limit",
            definition: "Maximum allowed greenhouse gas emissions per square foot for specific building types under NYC Local Law 97.",
            formula: "Emission Limit = Building Type Factor × Gross Floor Area",
            example: "Office building: 0.00758 tCO2e/sq ft × 100,000 sq ft = 758 tCO2e limit",
            category: "Emissions"
        },

        // Financial Definitions
        {
            term: "Net Present Value (NPV)",
            definition: "The difference between the present value of cash inflows and outflows over a project's lifetime.",
            formula: "NPV = Σ(Cash Flow / (1 + discount rate)^t) - Initial Investment",
            category: "Financial"
        },
        {
            term: "Internal Rate of Return (IRR)",
            definition: "The discount rate that makes the net present value of all cash flows equal to zero.",
            example: "15% IRR means the investment generates 15% annual returns",
            category: "Financial"
        },
        {
            term: "Payback Period",
            definition: "The time required for cumulative savings to equal the initial retrofit investment cost.",
            formula: "Payback Period = Initial Investment ÷ Annual Savings",
            example: "$100,000 investment with $20,000 annual savings = 5-year payback",
            category: "Financial"
        },
        {
            term: "CPACE Financing",
            definition: "Commercial Property Assessed Clean Energy financing that allows property owners to finance energy improvements through property tax assessments.",
            category: "Financial"
        },

        // Legal Definitions
        {
            term: "Local Law 97 (LL97)",
            definition: "NYC legislation requiring buildings over 25,000 sq ft to meet greenhouse gas emission limits or pay penalties starting in 2024.",
            category: "Legal"
        },
        {
            term: "LL97 Penalty Rate",
            definition: "Fine of $268 per metric ton of CO2e emissions above the building's allowable limit.",
            formula: "Annual Fine = Emissions Gap × $268",
            example: "50 tCO2e over limit × $268 = $13,400 annual fine",
            category: "Legal"
        },
        {
            term: "Building Emissions Gap",
            definition: "The amount by which a building's actual emissions exceed its LL97 allowable emissions limit.",
            formula: "Emissions Gap = Actual Emissions - LL97 Emission Limit",
            category: "Legal"
        },

        // Building Type Definitions
        {
            term: "Gross Floor Area",
            definition: "The total floor area within the building envelope, including basements, mezzanines, and penthouses.",
            category: "Building"
        },
        {
            term: "Mixed Use Building",
            definition: "A building that combines two or more different uses, such as residential, commercial, and retail spaces.",
            category: "Building"
        },
        {
            term: "Multifamily Housing",
            definition: "Residential buildings containing multiple separate housing units, typically apartments or condominiums.",
            category: "Building"
        }
    ];

    const tamData: TAMData[] = [
        {
            market: "NYC Building Energy Retrofit Market",
            size: "$2.8 Billion",
            unit: "Annual Market Size",
            description: "Total addressable market for energy retrofits in NYC buildings over 25,000 sq ft subject to Local Law 97",
            opportunity: "64,151 buildings requiring compliance improvements with average retrofit costs of $43,700 per building",
            source: "NYC Mayor's Office of Climate & Environmental Justice, 2023"
        },
        {
            market: "LL97 Non-Compliance Penalties",
            size: "$1.2 Billion",
            unit: "Annual Penalty Risk",
            description: "Total potential annual fines for all NYC buildings that fail to meet LL97 emission limits by 2030",
            opportunity: "Buildings can avoid 100% of penalties through strategic energy retrofits",
            source: "Urban Green Council Analysis, 2024"
        },
        {
            market: "CPACE Financing Market",
            size: "$850 Million",
            unit: "Available Capital",
            description: "Commercial Property Assessed Clean Energy financing available for NYC energy improvement projects",
            opportunity: "Up to 20-year financing terms with property tax payment structure",
            source: "NYC Economic Development Corporation, 2024"
        },
        {
            market: "Federal and State Incentives",
            size: "$420 Million",
            unit: "Annual Incentive Pool",
            description: "Combined federal tax credits, NYSERDA rebates, and Con Edison incentives for commercial energy efficiency",
            opportunity: "30% federal tax credit + up to $200,000 in state/utility rebates per project",
            source: "NYSERDA & U.S. Department of Energy, 2024"
        },
        {
            market: "Energy Cost Savings Potential",
            size: "$890 Million",
            unit: "Annual Savings",
            description: "Total potential annual energy cost savings across all NYC commercial buildings through retrofits",
            opportunity: "Average 25-40% reduction in energy costs post-retrofit",
            source: "NYC Benchmarking Data Analysis, 2023"
        },
        {
            market: "Carbon Reduction Market",
            size: "1.8 Million",
            unit: "Metric Tons CO2e",
            description: "Total greenhouse gas emission reduction potential from NYC building retrofits",
            opportunity: "Equivalent to removing 391,000 cars from roads annually",
            source: "NYC Climate Planning Office, 2024"
        }
    ];

    const filteredDefinitions = selectedCategory === 'All' 
        ? definitions 
        : definitions.filter(def => def.category === selectedCategory);

    const categories = ['All', 'Energy', 'Emissions', 'Financial', 'Legal', 'Building'];

    return (
        <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4">Knowledge Center</h2>
                
                {/* Tab Navigation */}
                <div className="flex space-x-1 bg-neutral-100 rounded-lg p-1">
                    <button
                        onClick={() => setActiveTab('definitions')}
                        className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                            activeTab === 'definitions'
                                ? 'bg-white text-primary-600 shadow-sm'
                                : 'text-neutral-600 hover:text-neutral-900'
                        }`}
                    >
                        📚 Definitions & Formulas
                    </button>
                    <button
                        onClick={() => setActiveTab('tam')}
                        className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                            activeTab === 'tam'
                                ? 'bg-white text-primary-600 shadow-sm'
                                : 'text-neutral-600 hover:text-neutral-900'
                        }`}
                    >
                        📊 Market Analysis (TAM)
                    </button>
                </div>
            </div>

            {activeTab === 'definitions' && (
                <div>
                    {/* Category Filter */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                            Filter by Category:
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {categories.map(category => (
                                <button
                                    key={category}
                                    onClick={() => setSelectedCategory(category)}
                                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                                        selectedCategory === category
                                            ? 'bg-primary-100 text-primary-800 border border-primary-200'
                                            : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                                    }`}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Definitions Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {filteredDefinitions.map((def, index) => (
                            <div key={index} className="border border-neutral-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between mb-2">
                                    <h3 className="text-lg font-semibold text-neutral-900">{def.term}</h3>
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                        def.category === 'Energy' ? 'bg-success-100 text-success-800' :
                                        def.category === 'Emissions' ? 'bg-danger-100 text-danger-800' :
                                        def.category === 'Financial' ? 'bg-primary-100 text-primary-800' :
                                        def.category === 'Legal' ? 'bg-accent-100 text-accent-800' :
                                        'bg-neutral-100 text-neutral-800'
                                    }`}>
                                        {def.category}
                                    </span>
                                </div>
                                
                                <p className="text-neutral-700 text-sm mb-3">{def.definition}</p>
                                
                                {def.formula && (
                                    <div className="mb-2">
                                        <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Formula:</span>
                                        <code className="block mt-1 p-2 bg-neutral-50 text-xs text-neutral-800 rounded font-mono">
                                            {def.formula}
                                        </code>
                                    </div>
                                )}
                                
                                {def.example && (
                                    <div>
                                        <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Example:</span>
                                        <p className="text-xs text-neutral-600 mt-1 italic">{def.example}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {activeTab === 'tam' && (
                <div>
                    <div className="mb-6">
                        <h3 className="text-xl font-semibold text-neutral-900 mb-2">Total Addressable Market Analysis</h3>
                        <p className="text-neutral-600">
                            Comprehensive market sizing and opportunity assessment for NYC energy retrofit sector
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {tamData.map((market, index) => (
                            <div key={index} className="border border-neutral-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                                <div className="mb-4">
                                    <h4 className="text-lg font-semibold text-neutral-900 mb-1">{market.market}</h4>
                                    <div className="flex items-baseline space-x-2">
                                        <span className="text-3xl font-bold text-primary-600">{market.size}</span>
                                        <span className="text-sm text-neutral-500">{market.unit}</span>
                                    </div>
                                </div>
                                
                                <p className="text-neutral-700 text-sm mb-4">{market.description}</p>
                                
                                <div className="bg-primary-50 p-3 rounded-lg mb-4">
                                    <h5 className="text-sm font-medium text-primary-900 mb-1">💡 Opportunity</h5>
                                    <p className="text-sm text-primary-800">{market.opportunity}</p>
                                </div>
                                
                                <div className="text-xs text-neutral-500">
                                    <strong>Source:</strong> {market.source}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Market Summary */}
                    <div className="mt-8 bg-gradient-to-r from-primary-50 to-primary-100 p-6 rounded-lg">
                        <h4 className="text-lg font-semibold text-neutral-900 mb-4">Market Summary & Investment Thesis</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div>
                                <h5 className="font-medium text-neutral-900 mb-2">🎯 Market Drivers</h5>
                                <ul className="text-neutral-700 space-y-1">
                                    <li>• LL97 compliance mandate (2024-2030)</li>
                                    <li>• Rising energy costs</li>
                                    <li>• Federal/state incentives</li>
                                    <li>• ESG requirements</li>
                                </ul>
                            </div>
                            <div>
                                <h5 className="font-medium text-neutral-900 mb-2">💰 Financial Opportunity</h5>
                                <ul className="text-neutral-700 space-y-1">
                                    <li>• $2.8B retrofit market size</li>
                                    <li>• 15-25% typical project IRR</li>
                                    <li>• 4-8 year payback periods</li>
                                    <li>• Up to 30% tax credit available</li>
                                </ul>
                            </div>
                            <div>
                                <h5 className="font-medium text-neutral-900 mb-2">🌱 Impact Potential</h5>
                                <ul className="text-neutral-700 space-y-1">
                                    <li>• 1.8M tons CO2e reduction</li>
                                    <li>• 25-40% energy cost savings</li>
                                    <li>• 64,151 buildings addressable</li>
                                    <li>• $890M annual savings potential</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DefinitionsAndTAM;
